import { formatHumanName, MedplumClient, resolveId } from "@medplum/core";
import { Practitioner, PractitionerRole } from "@medplum/fhirtypes";

import { MissingServicesFilterProvider } from "@/billing/types/missing-services";
import { QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";
import { PROVIDER_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";
import { getOrganizationReference, joinReferenceString, joinTokenString } from "@/utils/medplum";
import { fetchAllPaginatedResources } from "@/utils/pagination";

function hasProviderFilterRole(role: PractitionerRole): boolean {
  const code = role.code?.[0]?.coding?.[0]?.code;
  return !!code && (PROVIDER_PERMISSION_FLAGS as string[]).includes(code);
}

/**
 * Fetches the practice's active Practitioners eligible for the Missing Services Provider filter —
 * those holding one of `PROVIDER_PERMISSION_FLAGS`' roles (Admin, Admin Provider, Provider, or Doula:
 * the roles that can sign and lock an encounter) in the practice. Role codes are never a server-side
 * search param in this codebase; they are read off each role after fetching, matching
 * `fetchProvidersWithHoursInOrganization` in `app/scheduling/services/providers.ts`.
 */
export async function fetchMissingServicesFilterProviders(
  medplum: MedplumClient,
  practiceId: string,
): Promise<MissingServicesFilterProvider[]> {
  const rolesResult = await fetchAllPaginatedResources<PractitionerRole>(
    medplum,
    "PractitionerRole",
    {
      organization: getOrganizationReference(practiceId),
      active: true,
    },
  );
  const roles = rolesResult.success ? rolesResult.data : [];

  const practitionerIds = [
    ...new Set(
      roles.filter(hasProviderFilterRole).reduce((ids, role) => {
        const practitionerId = resolveId(role.practitioner);
        if (practitionerId) ids.push(practitionerId);
        return ids;
      }, [] as string[]),
    ),
  ];

  if (practitionerIds.length === 0) {
    return [];
  }

  const practitionersResult = await fetchAllPaginatedResources<Practitioner>(
    medplum,
    "Practitioner",
    {
      _id: practitionerIds.join(","),
    },
  );
  const practitioners = practitionersResult.success ? practitionersResult.data : [];

  return practitioners
    .filter((practitioner): practitioner is Practitioner & { id: string } => !!practitioner.id)
    .map((practitioner) => ({
      id: practitioner.id,
      label: formatHumanName(practitioner.name?.[0]) || "Unnamed Provider",
    }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
}

/**
 * `_tag` search value matching Encounters signed and locked by any of the selected Practitioners.
 *
 * The signing practitioner is read off the `EncounterSignedAndLockedBy` `meta.tag`, whose code is the
 * Practitioner reference string (see `finishEncounterWithSignedAndLockedBy`). Searching `participant=`
 * instead would match every role a Practitioner held on the Encounter — provider, doula,
 * others-present — so an Encounter someone merely attended would answer a filter on who signed it,
 * and the Provider column (which reads the tag) would disagree with the filter that returned the row.
 *
 * @param providerIds Practitioner ids selected in the Provider filter
 * @returns The `system|Practitioner/<id>` tokens comma-joined as one FHIR OR-list, or `undefined`
 * when no provider is selected
 */
export function buildSignedAndLockedByTagSearchValue(providerIds: string[]): string | undefined {
  if (providerIds.length === 0) return undefined;
  return providerIds
    .map(
      (id) =>
        joinTokenString(
          QuiltedHealthCodeSystems.EncounterSignedAndLockedBy,
          joinReferenceString("Practitioner", id),
        )!,
    )
    .join(",");
}
